import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Anchor, ArrowRight, BookOpen, Briefcase, Check, Compass, Flame, HandHeart, Heart, Home, KeyRound, Loader2, Pause, Play, RotateCcw, Scale, Shield, Sprout, Sun, Target, Users, X } from "lucide-react";
import { lpErr } from "@/components/portal/lp/lpApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/re/portal/lessons`;
const ICON = { heart: Heart, shield: Shield, target: Target, users: Users, key: KeyRound, compass: Compass, sprout: Sprout, scale: Scale, home: Home, briefcase: Briefcase, book: BookOpen, sun: Sun, anchor: Anchor, flame: Flame, hand: HandHeart };
const gold = "#d4a017";
const rise = { initial: { opacity: 0, y: 24, scale: 0.97 }, animate: { opacity: 1, y: 0, scale: 1 }, transition: { duration: 0.6, ease: "easeOut" } };

/** Text with the focus word lit up in gold and an animated arrow pointing at it. */
function Focus({ text, focus }) {
  const i = focus ? text.toLowerCase().indexOf(focus.toLowerCase()) : -1;
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <span className="whitespace-nowrap">
      <motion.span className="inline-block align-middle mr-0.5" initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: [0, 4, 0] }} transition={{ delay: 0.8, x: { repeat: Infinity, duration: 1.2 } }}>
        <ArrowRight className="h-5 w-5" style={{ color: gold }} />
      </motion.span>
      <motion.span initial={{ backgroundSize: "0% 40%" }} animate={{ backgroundSize: "100% 40%" }} transition={{ delay: 0.5, duration: 0.7 }}
        className="bg-no-repeat bg-bottom px-0.5" style={{ backgroundImage: `linear-gradient(${gold}66, ${gold}66)`, color: "#f5d77a" }}>{text.slice(i, i + focus.length)}</motion.span>
      </span>
      {text.slice(i + focus.length)}
    </>
  );
}

function Beat({ b }) {
  const Icon = ICON[b.icon] || Sun;
  if (b.type === "headline") return (
    <motion.div {...rise} className="flex items-center gap-4"><motion.div animate={{ rotate: [0, -8, 8, 0] }} transition={{ delay: 0.6, duration: 1 }}><Icon className="h-12 w-12 shrink-0" style={{ color: gold }} /></motion.div>
      <h2 className="text-2xl sm:text-3xl font-semibold leading-tight "><Focus text={b.text} focus={b.focus} /></h2></motion.div>);
  if (b.type === "symbol") return (
    <motion.div {...rise} className="flex flex-col items-center text-center gap-3 py-2">
      <motion.div animate={{ scale: [1, 1.12, 1] }} transition={{ repeat: Infinity, duration: 2.4 }} className="rounded-full p-5" style={{ background: `${gold}1f`, boxShadow: `0 0 60px ${gold}33` }}><Icon className="h-14 w-14" style={{ color: gold }} /></motion.div>
      <p className="text-lg text-white/90"><Focus text={b.text} focus={b.focus} /></p></motion.div>);
  if (b.type === "compare") return (
    <motion.div {...rise} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
      <div className="rounded-md border border-red-400/30 bg-red-400/10 p-3 text-sm text-red-100 line-through decoration-red-300/60">{b.left || "Before"}</div>
      <motion.div initial={{ x: -8, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.5 }}><ArrowRight className="h-6 w-6" style={{ color: gold }} /></motion.div>
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.9 }} className="rounded-md border p-3 text-sm font-medium" style={{ borderColor: `${gold}88`, background: `${gold}1a`, color: "#f5d77a" }}>{b.right || b.text}</motion.div>
    </motion.div>);
  if (b.type === "quote") return (
    <motion.blockquote {...rise} className="border-l-4 pl-4 text-xl italic text-white/90" style={{ borderColor: gold }}>“<Focus text={b.text} focus={b.focus} />”</motion.blockquote>);
  if (b.type === "list") return (
    <motion.div {...rise} className="space-y-2">
      <p className="text-white/80">{b.text}</p>
      {(b.items || []).map((it, k) => (
        <motion.div key={it} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + k * 0.5 }} className="flex items-center gap-2 rounded-md bg-white/[0.04] px-3 py-2">
          <Check className="h-4 w-4 shrink-0" style={{ color: gold }} /><span>{it}</span></motion.div>
      ))}
    </motion.div>);
  return (
    <motion.div {...rise} className="flex items-start gap-3"><Icon className="h-6 w-6 shrink-0 mt-1" style={{ color: gold }} /><p className="text-lg "><Focus text={b.text} focus={b.focus} /></p></motion.div>);
}

function Part({ part, idx, total, audioUrl, onReady }) {
  const ref = useRef(null);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [fallback, setFallback] = useState(null);
  useEffect(() => { const a = ref.current; a?.play().then(() => setPlaying(true)).catch(() => setPlaying(false)); }, [audioUrl]);
  useEffect(() => { if (fallback === null) return undefined; if (fallback <= 0) { setEnded(true); return undefined; } const id = setTimeout(() => setFallback(fallback - 1), 1000); return () => clearTimeout(id); }, [fallback]);
  const endRef = useRef(null);
  const frac = ended ? 1 : fallback !== null ? 1 - fallback / 90 : dur ? t / dur : 0;
  const shown = part.beats.filter((_, k) => k === 0 || frac >= k / part.beats.length);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [shown.length, ended]);
  const toggle = () => { const a = ref.current; if (!a) return; if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); } };
  return (
    <div className="space-y-6" data-testid={`lesson-part-${idx}`}>
      <audio ref={ref} src={audioUrl} preload="auto" onTimeUpdate={(e) => setT(e.target.currentTime)} onLoadedMetadata={(e) => setDur(e.target.duration)}
        onEnded={() => { setEnded(true); setPlaying(false); }} onError={() => setFallback((f) => (f === null ? 90 : f))} />
      <div className="text-xs uppercase tracking-[0.3em]" style={{ color: gold }}>Part {idx + 1} of {total}</div>
      <motion.h1 key={part.heading} {...rise} className="text-3xl sm:text-4xl font-semibold">{part.heading}</motion.h1>
      <div className="min-h-[300px] space-y-8 pt-2"><AnimatePresence>{shown.map((b, k) => <Beat key={`${idx}-${k}`} b={b} />)}</AnimatePresence></div>
      <div className="h-1 w-full rounded bg-white/10 overflow-hidden"><motion.div className="h-full" style={{ background: gold }} animate={{ width: `${Math.round(frac * 100)}%` }} /></div>
      <div className="flex items-center gap-3">
        <button type="button" onClick={toggle} data-testid="lesson-play" className="rounded-full p-3 border border-white/20 hover:border-white/50" aria-label={playing ? "Pause" : "Play"}>{playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}</button>
        <button type="button" onClick={() => { if (ref.current) { ref.current.currentTime = 0; ref.current.play(); setPlaying(true); } }} data-testid="lesson-replay" className="rounded-full p-3 border border-white/20 hover:border-white/50" aria-label="Replay"><RotateCcw className="h-5 w-5" /></button>
        <button type="button" disabled={!ended} onClick={onReady} data-testid="lesson-continue" className="ml-auto rounded-md px-5 py-3 font-semibold text-black disabled:opacity-40" style={{ background: gold }}>
          {ended ? "Quick check →" : fallback !== null ? `Keep reading · ${fallback}s` : "Listen to the end"}
        </button>
      </div>
      {fallback !== null && <p className="text-sm text-white/70 leading-relaxed">{part.narration}</p>}
      <div ref={endRef} />
    </div>
  );
}

function Check5({ statement, onAnswer, result, onNext, last }) {
  return (
    <motion.div {...rise} className="space-y-6" data-testid="lesson-check">
      <div className="text-xs uppercase tracking-[0.3em]" style={{ color: gold }}>True or false?</div>
      <p className="text-2xl font-semibold leading-snug" data-testid="lesson-check-statement">{statement}</p>
      {!result ? (
        <div className="grid grid-cols-2 gap-3">
          <button type="button" onClick={() => onAnswer(true)} data-testid="lesson-true" className="rounded-md py-5 text-lg font-semibold border border-emerald-400/50 bg-emerald-400/10 hover:bg-emerald-400/20">True</button>
          <button type="button" onClick={() => onAnswer(false)} data-testid="lesson-false" className="rounded-md py-5 text-lg font-semibold border border-red-400/50 bg-red-400/10 hover:bg-red-400/20">False</button>
        </div>
      ) : (
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} data-testid="lesson-feedback"
          className={`rounded-md p-4 border ${result.correct ? "border-emerald-400/50 bg-emerald-400/10" : "border-amber-400/50 bg-amber-400/10"}`}>
          <div className="flex items-center gap-2 font-semibold">{result.correct ? <Check className="h-5 w-5 text-emerald-300" /> : <X className="h-5 w-5 text-amber-300" />}{result.correct ? "Nice — you got it." : `Not quite — it was ${result.answer ? "true" : "false"}.`}</div>
          {result.explain && <p className="mt-1 text-sm text-white/80">{result.explain}</p>}
          <button type="button" onClick={onNext} data-testid="lesson-next" className="mt-4 rounded-md px-5 py-3 font-semibold text-black" style={{ background: gold }}>{last ? "See my score" : "Next part →"}</button>
        </motion.div>
      )}
    </motion.div>
  );
}

/** Homework lesson: 5 narrated, animated parts, each with a true/false check; 4/5 unlocks the pillar question. */
export default function LessonPlayer({ token, assignmentId, onFinished }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(null);
  const [phase, setPhase] = useState(null);
  const [result, setResult] = useState(null);
  const load = useCallback(() => axios.get(`${API}/${assignmentId}`, { params: { token } }).then((r) => setD(r.data)).catch((e) => setError(lpErr(e))), [assignmentId, token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (d && !d.preparing && phase === null) setPhase(d.progress.done ? "score" : "intro"); }, [d, phase]);
  useEffect(() => { if (!d?.preparing) return undefined; const id = setTimeout(load, 5000); return () => clearTimeout(id); }, [d, load]);
  if (error) return <p className="text-red-300" data-testid="lesson-error">{error}</p>;
  if (!d || d.preparing || phase === null) return <div data-testid="lesson-preparing" className="flex flex-col items-center gap-3 py-20 text-white/70"><Loader2 className="h-8 w-8 animate-spin" style={{ color: gold }} />Preparing your lesson… this takes about a minute the first time.</div>;
  const p = d.progress;
  const first = p.answers.findIndex((x) => x === null);
  if (phase === "score") {
    return (
      <motion.div {...rise} className="text-center space-y-5 py-10" data-testid="lesson-score">
        <div className="text-6xl font-semibold" style={{ color: p.passed ? gold : "#fbbf24" }}>{p.score}/{d.parts.length}</div>
        <p className="text-xl font-semibold">{p.passed ? "Pillar unlocked!" : "Good effort."}</p>
        <p className="text-white/70">{p.passed ? "You're ready for this week's question." : `You need ${d.pass_mark} of ${d.parts.length}. Your case manager will look at your score and may ask you to go through it again.`}</p>
        {p.passed && <button type="button" onClick={onFinished} data-testid="lesson-go-question" className="rounded-md px-6 py-3 font-semibold text-black" style={{ background: gold }}>Answer the question</button>}
      </motion.div>
    );
  }
  if (phase === "intro") {
    return (
      <motion.div {...rise} className="space-y-6 py-6" data-testid="lesson-intro">
        <div className="text-xs uppercase tracking-[0.3em]" style={{ color: gold }}>Homework lesson · about 15 minutes</div>
        <h1 className="text-4xl font-semibold">{d.title}</h1>
        <p className="text-white/70">5 short parts. Listen, watch, then answer one true-or-false after each. Get {d.pass_mark} of 5 to unlock this week's pillar question.</p>
        <div className="flex gap-2">{d.parts.map((x, k) => <div key={k} className={`h-2 flex-1 rounded ${p.answers[k] === null ? "bg-white/15" : "bg-[#d4a017]"}`} />)}</div>
        <button type="button" onClick={() => { setStep(first); setPhase("watch"); }} data-testid="lesson-start" className="w-full rounded-md py-4 text-lg font-semibold text-black" style={{ background: gold }}>
          {first > 0 ? `Continue · part ${first + 1}` : "Start lesson"}
        </button>
      </motion.div>
    );
  }
  const part = d.parts[step];
  const answer = async (val) => {
    try { const r = await axios.post(`${API}/${assignmentId}/answer`, { part: step, answer: val }, { params: { token } }); setResult(r.data); setD({ ...d, progress: r.data.progress }); }
    catch (e) { setError(lpErr(e)); }
  };
  const next = () => {
    setResult(null);
    const n = d.progress.answers.findIndex((x) => x === null);
    if (n < 0) setPhase("score"); else { setStep(n); setPhase("watch"); }
  };
  return (
    <div className="space-y-4">
      <div className="flex gap-1.5">{d.parts.map((x, k) => <div key={k} className={`h-1.5 flex-1 rounded ${k === step ? "bg-white/60" : d.progress.answers[k] === null ? "bg-white/15" : "bg-[#d4a017]"}`} />)}</div>
      {phase === "watch"
        ? <Part key={step} part={part} idx={step} total={d.parts.length} audioUrl={`${API}/${assignmentId}/audio/${step}?v=${encodeURIComponent(d.voice || "")}&token=${encodeURIComponent(token)}`} onReady={() => setPhase("check")} />
        : <Check5 statement={part.check.statement} onAnswer={answer} result={result} onNext={next} last={d.progress.answers.every((x) => x !== null)} />}
    </div>
  );
}
